import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import PageHeader from '../PageHeader';
import SmartImage from '../ui/SmartImage';
import MediaSkeleton from '../ui/MediaSkeleton';

function MediaSection() {
    const { jenis } = useParams();
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError('');

        fetch(`${process.env.REACT_APP_BACKEND_URL}/articles?jenis=${jenis}`, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return response.json();
            })
            .then((data) => setArticles(Array.isArray(data.data) ? data.data : []))
            .catch((fetchError) => {
                if (fetchError.name !== 'AbortError') {
                    console.error('Error fetching articles:', fetchError);
                    setError('Konten belum dapat dimuat. Silakan coba kembali.');
                }
            })
            .finally(() => setLoading(false));

        return () => controller.abort();
    }, [jenis]);

    const heading = jenis === 'artikel' ? 'Artikel' : 'Berita';

    return (
        <>
            <PageHeader title="Media" />
            <main className="min-h-screen bg-slate-950 py-12 sm:py-16">
                <div className="container mx-auto max-w-6xl px-4 sm:px-6">
                    <div className="mb-9">
                        <span className="text-xs font-semibold uppercase tracking-[0.24em] text-blue-400">Media Perusahaan</span>
                        <h1 className="mt-2 text-3xl font-bold text-white sm:text-4xl">{heading}</h1>
                    </div>

                    {loading && <MediaSkeleton count={3} />}

                    {!loading && error && (
                        <div className="rounded-2xl border border-red-900/60 bg-red-950/30 p-8 text-center text-red-200">{error}</div>
                    )}

                    {!loading && !error && articles.length === 0 && (
                        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-10 text-center text-slate-400">Belum ada {heading.toLowerCase()} yang tersedia.</div>
                    )}

                    {!loading && !error && articles.length > 0 && (
                        <div className="space-y-7">
                            {articles.map((article) => (
                                <article key={article.id || article.slug} className="group overflow-hidden rounded-[26px] border border-slate-800 bg-slate-900/90 shadow-2xl shadow-black/10 transition hover:border-slate-700 md:flex md:min-h-[250px]">
                                    <div className="h-56 w-full flex-none overflow-hidden bg-slate-800 md:h-auto md:w-2/5">
                                        <SmartImage
                                            src={article.image_urls?.[0] || '/default-avatar.jpg'}
                                            alt={article.title}
                                            wrapperClassName="h-full w-full"
                                            className="h-full w-full object-cover object-center transition duration-500 group-hover:scale-[1.03]"
                                        />
                                    </div>
                                    <div className="flex min-w-0 flex-1 flex-col justify-between p-6 sm:p-7">
                                        <div>
                                            <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
                                                <span className="rounded-full bg-blue-500/10 px-3 py-1 font-semibold uppercase tracking-wide text-blue-300">{article.jenis || heading}</span>
                                                <span className="text-slate-600">•</span>
                                                <span className="text-slate-400">{article.published_date ? format(new Date(article.published_date), 'MMMM dd, yyyy') : '-'}</span>
                                            </div>
                                            <Link to={`/media/${article.jenis}/${article.slug}`} className="block text-xl font-bold leading-snug text-white transition hover:text-blue-300 sm:text-2xl">
                                                {article.title}
                                            </Link>
                                            <p className="mt-3 line-clamp-3 text-sm leading-7 text-slate-400 sm:text-base">
                                                {article.description ? article.description.slice(0, 350) : 'Konten tidak tersedia'}
                                            </p>
                                        </div>
                                        <div className="mt-6 flex items-center border-t border-slate-800 pt-5">
                                            <Link
                                                to={`/media/${article.jenis}/${article.slug}`}
                                                className="inline-flex items-center gap-2 rounded-full border border-blue-400/20 bg-blue-500/10 px-4 py-2 text-sm font-semibold text-blue-300 transition hover:border-blue-300/40 hover:bg-blue-500/20 hover:text-white"
                                            >
                                                Baca selengkapnya <span aria-hidden="true">&rarr;</span>
                                            </Link>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </div>
            </main>
        </>
    );
}

export default MediaSection;
